import { Controller } from "@hotwired/stimulus"

// Usage:
// <div data-controller="multi-value-input" data-multi-value-input-name-value="lead[location_state][]">
//   <input data-multi-value-input-target="input" ... >
//   <div data-multi-value-input-target="list"></div>
// </div>

export default class extends Controller {
    static targets = ["input", "list", "toggle", "resetBtn"]
    static values = { name: String, exclusion: String, skipSplitting: String }

    connect() {
        this.valuesArray = []
        this.controllerId = this.element.dataset.multiValueInputId;
        this.updateResetVisibility();

        document.addEventListener('bulk-add-values', (event) => {
            const { values, exclusion, targetId, restoration, preserveCase } = event.detail;

            if (values === undefined) return;
            if (targetId !== this.controllerId) return;

            const unique = [...new Set(values.map(v => {
                if (this.nameValue === 'search[company_domain][values][]') {
                    return v
                }
                if (restoration !== undefined && restoration) {
                    return v
                }
                // A closed-list field has already had its paste checked against
                // the taxonomy and handed back spelled the way the taxonomy
                // spells it. Title-casing it here turned "HubSpot" into
                // "Hubspot" and "United States" into "United states" - values
                // the search then matched nothing on.
                if (preserveCase) {
                    return v
                }

                return v.charAt(0).toUpperCase() + v.slice(1).toLowerCase()
            }))];

            // LinkedIn exclusion field: always add as exclusions
            const effectiveExclusion = this.nameValue === 'search[lead_linkedin_url][values][]' ? true : exclusion;
            this.bulkAddValues(unique, effectiveExclusion);
        });

        document.addEventListener('removed_filters', (event) => {
            // The chips and the hidden inputs are gone by now; what is left is
            // this controller's own memory of them, plus the two bits of chrome
            // that were sized against it.
            this.valuesArray = [];
            if (this.hasToggleTarget) this.toggleTarget.innerHTML = '';
            this.updateResetVisibility();
        });
    }

    disconnect() {
        document.removeEventListener('bulk-add-values', this.disconnect);
        document.removeEventListener('removed_filters', this.disconnect);
    }

    fireEvent(element, event_name) {
        if (element === undefined) return
        if (event_name === undefined) return

        element.dispatchEvent(
            new CustomEvent(event_name, { bubbles: true })
        );
    }

    addValue(event) {
        const isKeyboard = event.type === "keydown" || event.type === "keypress"
        if (isKeyboard && event.key !== "Enter") return
        if (isKeyboard) event.preventDefault()
        let exclusionValue = event.currentTarget.dataset.multiValueInputExclusionValue === 'true'
        // LinkedIn exclusion field: always treat values as exclusions (ignore Include button)
        if (this.nameValue === 'search[lead_linkedin_url][values][]') {
            exclusionValue = true
        }

        const handleValue = (raw) => {
            if (!raw) return

            const processValue = (val) => {
                let exclusion = exclusionValue || val.startsWith("!")
                val = val.replace(/^!/, "")

                if (this.nameValue === 'search[company_domain][values][]') {
                    val = this.extractDomain((val))
                }
                if (this.nameValue === 'search[lead_linkedin_url][values][]') {
                    val = this.extractLinkedInSlug(val)
                }


                if (!this.valuesArray.some(v => v.value.toLowerCase() === val.toLowerCase())) {
                    this.valuesArray.push({ value: val, exclusion })
                    this.renderTag(val, exclusion)
                }
            }

            processValue(raw)
            this.updateHiddenInputs()
            this.inputTarget.value = ""
        }

        const raw = event.currentTarget.dataset.value?.trim() || this.inputTarget.value?.trim() || ""
        const skipSplitting = event.currentTarget.dataset.multiValueInputSkipSplittingValue
        if (raw.includes(',') &&  skipSplitting !== 'true') {
            raw.split(',').map(v => v.trim()).forEach((v) => handleValue(v))
        } else {
            handleValue(raw)
        }
    }

    clearAll(event) {
        event.preventDefault()

        this.valuesArray = []
        this.listTarget.innerHTML = ""
        this.inputTarget.value = ""

        const fieldName = this.nameValue
        this.element.querySelectorAll(`input[name="${fieldName}"]`).forEach(el => el.remove())

        this.updateResetVisibility()
        this.fireEvent(this.element.closest('#filters-wrapper'), 'filters-changed')
    }

    removeValue(event) {
        const value = event.target.dataset.value
        this.valuesArray = this.valuesArray.filter(v => v.value !== value)
        event.target.closest("[data-tag]")?.remove()
        this.updateHiddenInputs()
        this.updateCollapsedTags()
        this.updateResetVisibility()
    }

    renderTag(value, exclusion = false) {
        this.listTarget.appendChild(this.buildTagElement(value, exclusion))
        this.updateCollapsedTags()
        this.updateResetVisibility()

        this.fireEvent(this.element.closest('#filters-wrapper'), 'filters-changed')
    }

    updateResetVisibility() {
        if (!this.hasResetBtnTarget) return
        const hasFilters = this.valuesArray.length > 0
        this.resetBtnTarget.classList.toggle("hidden", !hasFilters)
    }

    updateCollapsedTags() {
        const tags = Array.from(this.listTarget.querySelectorAll('[data-tag="value"]'))
        const maxVisible = 10

        // Clear old button
        this.toggleTarget.innerHTML = ''

        if (tags.length <= maxVisible) {
            tags.forEach(tag => tag.style.display = '')
            return
        }

        // Hide tags after 10th
        tags.forEach((tag, index) => {
            tag.style.display = index < maxVisible ? '' : 'none'
        })

        // Create toggle button
        const btn = document.createElement("button")
        btn.type = 'button'
        btn.className = "px-2.5 py-1 text-xs btn--primary rounded"
        btn.textContent = `Show ${tags.length - maxVisible} more`

        btn.addEventListener("click", () => {
            const hidden = tags[maxVisible].style.display === "none"
            tags.forEach((tag, index) => {
                if (index >= maxVisible) tag.style.display = hidden ? '' : 'none'
            })
            btn.textContent = hidden
                ? "Show less"
                : `Show ${tags.length - maxVisible} more`
        })

        this.toggleTarget.appendChild(btn)
    }

    updateHiddenInputs() {
        const fieldName = this.nameValue
        this.element.querySelectorAll(`input[name="${fieldName}"]`).forEach(el => el.remove())

        this.valuesArray.forEach(({ value, exclusion }) => {
            const hidden = document.createElement("input")
            hidden.type = "hidden"
            hidden.name = fieldName
            hidden.id = 'list-value'
            hidden.value = exclusion && !value.startsWith("!") ? `!${value}` : value
            this.element.appendChild(hidden)
        })

        this.fireEvent(this.element.closest('#filters-wrapper'), 'values-changed')
        this.fireEvent(this.element.closest('#filters-wrapper'), 'filters-changed')
    }

    bulkAddValues(values, exclusion = false) {
        if (!values.length) return;

        const fragment = document.createDocumentFragment();
        const newValues = [];
        // Same guard addValue() has always had. Without it a paste, or a saved
        // search restored over a field that already holds values, produces two
        // identical chips and two identical hidden inputs - so the same value
        // travels twice to the search API.
        const seen = new Set(this.valuesArray.map(v => v.value.toLowerCase()));

        for (let raw of values) {
            if (!raw) continue;

            const exclusionVal = exclusion || raw.startsWith("!");
            let val = raw.replace(/^!/, "");

            if (this.nameValue === 'search[company_domain][values][]') {
                val = this.extractDomain(val);
            }
            if (this.nameValue === 'search[lead_linkedin_url][values][]') {
                val = this.extractLinkedInSlug(val);
            }

            if (seen.has(val.toLowerCase())) continue;
            seen.add(val.toLowerCase());

            newValues.push({ value: val, exclusion: exclusionVal });

            fragment.appendChild(this.buildTagElement(val, exclusionVal));
        }

        if (!newValues.length) return;

        this.listTarget.appendChild(fragment);

        this.valuesArray.push(...newValues);

        this.updateHiddenInputs();
        this.updateCollapsedTags()
        this.updateResetVisibility()
    }

    buildTagElement(value, exclusion = false) {
        const tag = document.createElement("div");
        tag.dataset.tag = "value";
        tag.dataset.value = value;

        // v9: the accent means "keep this", red means "not this". The look
        // itself lives in .lf-tag in the stylesheet.
        tag.className = "lf-tag " + (exclusion ? "lf-tag--exclude" : "lf-tag--include");

        const textNode = document.createTextNode(value);
        tag.appendChild(textNode);

        const btn = document.createElement("button");
        btn.type = "button";
        btn.dataset.action = "click->multi-value-input#removeValue";
        btn.dataset.value = value;
        btn.setAttribute("aria-label", `Remove ${value}`);
        btn.textContent = "×";

        tag.appendChild(btn);

        return tag;
    }

    removeSubdomain(value) {
        return value.replace(/^www\./, '');
    }

    extractDomain(url) {
        try {
            const normalizedUrl = url.startsWith('http') ? url : `https://${url}`;
            const { hostname } = new URL(normalizedUrl);
            return this.removeSubdomain(hostname)
        } catch {
            return this.removeSubdomain(url)
        }
    }

    extractLinkedInSlug(val) {
        const trimmed = val.trim();
        const linkedInInRegex = /^https?:\/\/(www\.)?linkedin\.com\/in\/(.+)$/i;
        const match = trimmed.match(linkedInInRegex);
        if (match) {
            return match[2].replace(/\/$/, '');
        }
        return trimmed;
    }
}